import { hasSetupDetails } from './workspace/setupPresence.js'
import { resolvePositionListStage, shouldShowPositionListProject } from './positionListStage.js'

export const ASSET_IDEA_SOURCE = 'Ideas List'
export function normalizeIdeaSymbol(value) {
  const symbol = String(value || '').trim().toUpperCase().replace(/\s+/g, '')
  if (!/^[A-Z0-9][A-Z0-9.^/-]{0,39}$/.test(symbol)) throw new Error('Select an asset before adding it to Ideas List.')
  return symbol
}

async function findCurrentProject(client, userId, symbol) {
  const { data, error } = await client.from('position_projects').select('*')
    .eq('user_id', userId).eq('symbol', symbol).eq('is_draft', false)
    .in('stage', ['idea', 'planned', 'working', 'active']).order('created_at', { ascending: false })
  if (error) throw new Error('Could not check this asset’s current stage. Please try again.')
  const visible = (data || []).filter(project => shouldShowPositionListProject(project))
  return visible.find(project => resolvePositionListStage(project) === 'active')
    || visible.find(project => resolvePositionListStage(project) === 'working') || visible[0] || null
}

export async function addAssetIdea(client, userId, value) {
  if (!userId) throw new Error('Sign in before adding an asset to Ideas List.')
  const symbol = normalizeIdeaSymbol(value)
  const existing = await findCurrentProject(client, userId, symbol)
  if (existing) return { project: existing, created: false, stage: resolvePositionListStage(existing) }
  const { data, error } = await client.from('position_projects').insert({
    user_id: userId, symbol, stage: 'idea', is_draft: false,
    direction: 'LONG', setup_status: 'idea', setup_source: ASSET_IDEA_SOURCE,
  }).select('*').single()
  if (!error) return { project: data, created: true, stage: 'idea' }
  if (error.code === '23505') {
    const concurrent = await findCurrentProject(client, userId, symbol)
    if (concurrent) return { project: concurrent, created: false, stage: resolvePositionListStage(concurrent) }
  }
  throw new Error('Could not add this asset to Ideas List. Please try again.')
}

// Only direct, setup-free saves qualify for removal without the setup gate.
export function isSetupFreeAssetIdea(project) {
  return project?.stage === 'idea'
    && project.setup_status === 'idea'
    && project.setup_source === ASSET_IDEA_SOURCE
    && project.is_draft === false
    && !project.trade_id
    && !project.entry_type
    && !hasSetupDetails(project)
}

export async function deleteAssetIdeaProjects(client, userId, projectIds, verify, beforeDelete = null) {
  if (!userId) throw new Error('Sign in before removing an idea.')
  const ids = [...new Set(projectIds.filter(Boolean))]
  if (!ids.length) return []
  const { data: projects, error } = await client.from('position_projects').select('*')
    .eq('user_id', userId).in('id', ids)
  if (error) throw new Error('Could not check saved setups. Please try again.')
  if (projects?.length !== ids.length || projects.some(project => resolvePositionListStage(project) !== 'idea')) {
    throw new Error('The selected ideas changed. Refresh the list and try again.')
  }
  const versionFilter = projects.map(project =>
    `and(id.eq.${JSON.stringify(project.id)},updated_at.eq.${JSON.stringify(project.updated_at)})`,
  ).join(',')
  const setupFree = projects.every(project => isSetupFreeAssetIdea(project) && project.updated_at)
  if (!setupFree) {
    const { verified, error: verificationError } = await verify()
    if (!verified) throw new Error(verificationError || 'Biometric verification failed')
  }
  if (beforeDelete) {
    // A delayed removal must retain the exact versions the operator reviewed.
    if (projects.some(project => !project.updated_at)) throw new Error('Could not verify idea versions. Refresh the list and try again.')
    if (!await beforeDelete()) return null
  }
  let deletion = client.from('position_projects').delete().eq('user_id', userId).in('id', ids)
  if (setupFree || beforeDelete) {
    // The existing database trigger advances updated_at on every update.
    // A setup saved after the read must not be deleted without verification.
    deletion = deletion.or(versionFilter)
  }
  const { data: deleted, error: deleteError } = await deletion.select('id')
  if (deleteError) throw deleteError
  return (deleted || []).map(project => project.id)
}
